import {
  type ColumnType,
  type ColumnsType,
  type SorterResult,
  Space,
  Table,
  type TableProps,
  Text,
} from '@/components';
import { useDevToolsStorage } from '@/features/dev-tools/_hooks/local-storage';
import { formatOwnershipRatioPercentage } from '@/features/mold/utils';
import { useProjectNameColumn } from '@/features/project/_components/ProjectNameColumn';
import {
  type RequestProjectsParams,
  useProjects,
} from '@/features/project/_hooks/project';
import { useHandle403ErrorWhenSearching as useHandle403ErrorWhenSearchingForPS } from '@/features/public-site/hooks';
import { useLocaledAmount } from '@/providers';
import {
  type FormatValueByUnitOptions,
  NEST_ITEM_CONNECTOR,
  formatFraction,
  formatValueByUnit,
  handleZeroValueWithNegativeCount,
  isPS,
  lowerCamelStringToSnake,
} from '@/utils';
import { ReviewTypeLabelModifier } from '@/utils/enum';
import { type FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import { useProjectsPageSearchQueryStates } from '../../_hooks/search-projects/search-query-states';

import {
  BusinessFormLabelModifier,
  CandidateReasonOfInspectionByExternalExpertModifier,
  OperationFormLabelModifier,
} from '../_models/project';
import {
  PROJECT_TABLE_COLUMN_KEYS,
  ProjectTableColumnLabelModifier,
  PublishedLabelModifier,
} from '../_models/project-filter';
import type { ProjectListElementModel } from '../_models/projects';
import { BudgetAmountGraph } from './BudgetAmountGraph';
import { ProjectsTableSkeleton } from './ProjectsTableSkeleton';

type Project = z.infer<typeof ProjectListElementModel>;

type Props = {
  policyId?: string;
  columnsSettings: string[];
  tableActionsColumn: ColumnType<Project>;
  pageSize?: number;
  scrollToTop?: () => void;
  requestParams: RequestProjectsParams;
  setSorting: (sorting: string) => void;
};

const getApiOrderingKey = (key: string): string => {
  switch (key) {
    case 'ministryName':
      return lowerCamelStringToSnake('ministry');
    case 'previousYearExecutedAmounts':
      return lowerCamelStringToSnake(
        'previousYearExecutedAmountDataTotalAmount',
      );
    case 'administrativeExpenses':
    case 'previousYearAdministrativeExpense':
      return lowerCamelStringToSnake('administrativeExpense');
    case 'amountReturnedToTheNationalTreasury':
    case 'previousYearAmountReturnedToTheNationalTreasury':
      return lowerCamelStringToSnake('amountReturnedToTheNationalTreasury');
    default:
      return lowerCamelStringToSnake(key);
  }
};

export const ProjectsTable: FC<Props> = (props) => {
  const {
    policyId,
    columnsSettings,
    tableActionsColumn,
    requestParams,
    pageSize,
    scrollToTop,
    setSorting,
  } = props;
  const { t } = useTranslation();
  const { searchQueryStates, setSearchQueryStates } =
    useProjectsPageSearchQueryStates();
  const { unit } = useLocaledAmount();
  const { devtoolState } = useDevToolsStorage();
  const isDebugMode = devtoolState?.isDebugMode;

  const [childTablePageNumber, setChildTablePageNumber] = useState('1');

  const projectsData = useProjects({
    params: {
      ...requestParams,
      policy: policyId,
      ...(policyId ? { page: childTablePageNumber } : {}),
      pageSize: searchQueryStates.pageSize.toString(),
    },
  });

  const projectNameColumn = useProjectNameColumn({
    title: ProjectTableColumnLabelModifier.name,
    dataCount: projectsData.data?.results.length ?? 0,
  });

  const getSortOrder = (
    key: keyof Project | string,
  ): 'ascend' | 'descend' | null => {
    const sortingKey: string = getApiOrderingKey(key);
    if (requestParams.ordering === sortingKey) {
      return 'ascend';
    }
    if (requestParams.ordering === `-${sortingKey}`) {
      return 'descend';
    }
    return null;
  };

  const renderAmountText = (
    value: Parameters<typeof formatValueByUnit>[0],
    operation: FormatValueByUnitOptions['operation'] = 'round',
  ) => {
    return formatValueByUnit(value, {
      operation,
      unit,
      showUnitLabel: true,
    });
  };

  // keyは表示設定のkeyと連動しているので変更する時は注意
  const allColumns: ColumnsType<Project> = [
    projectNameColumn,
    {
      title: ProjectTableColumnLabelModifier.isPublished,
      key: PROJECT_TABLE_COLUMN_KEYS.IS_PUBLISHED,
      dataIndex: 'isPublished',
      defaultSortOrder: getSortOrder('isPublished'),
      sorter: true,
      width: 100,
      fixed: 'left',
      render: (_, render) => {
        return PublishedLabelModifier[render.isPublished ? 'true' : 'false'];
      },
    },
    ...(requestParams.sheetType === 'RS' &&
    !requestParams.skipAdministrativeReformTeamEvaluation
      ? ([
          {
            title:
              ProjectTableColumnLabelModifier.inspectionByExternalExpertReviewType,
            key: PROJECT_TABLE_COLUMN_KEYS.INSPECTION_BY_EXTERNAL_EXPERT_REVIEW_TYPE,
            dataIndex: [
              'inspectionEvaluation',
              'inspectionByExternalExpert',
              'reviewType',
            ],
            width: 128,
            render: (_, record) => {
              const reviewType = record.inspectionByExternalExpertReviewType;
              return (
                <Text
                  text={reviewType ? ReviewTypeLabelModifier[reviewType] : null}
                  variant="body"
                  weight="regular"
                />
              );
            },
          },
        ] satisfies ColumnsType<Project>)
      : []),
    ...(requestParams.sheetType === 'RS' &&
    !requestParams.skipAdministrativeReformTeamEvaluation &&
    !isPS()
      ? ([
          {
            title:
              ProjectTableColumnLabelModifier.candidateReasonsOfInspectionByExternalExpert,
            key: PROJECT_TABLE_COLUMN_KEYS.CANDIDATE_REASONS_OF_INSPECTION_BY_EXTERNAL_EXPERT,
            dataIndex: 'candidateReasonsOfInspectionByExternalExpert',
            width: 184,
            render: (_, { candidateReasonsOfInspectionByExternalExpert }) => {
              const value = candidateReasonsOfInspectionByExternalExpert?.length
                ? candidateReasonsOfInspectionByExternalExpert
                    ?.map(
                      (reason) =>
                        CandidateReasonOfInspectionByExternalExpertModifier[
                          reason
                        ],
                    )
                    .join('\n')
                : null;
              return <Text text={value} variant="body" weight="regular" />;
            },
          },
          {
            title: ProjectTableColumnLabelModifier.lastImplementedFiscalYear,
            key: PROJECT_TABLE_COLUMN_KEYS.LAST_IMPLEMENTED_FISCAL_YEAR,
            width: 130,
            render: (_, record) => {
              return (
                <Text
                  text={record?.lastImplementedFiscalYear}
                  variant="body"
                  weight="regular"
                />
              );
            },
          },
        ] satisfies ColumnsType<Project>)
      : []),
    {
      title: ProjectTableColumnLabelModifier.ministryName,
      dataIndex: 'ministryName',
      key: PROJECT_TABLE_COLUMN_KEYS.MINISTRY_NAME,
      width: 200,
      defaultSortOrder: getSortOrder('ministryName'),
      sorter: true,
      render: (_, record) => {
        return (
          <Text text={record.ministryName} variant="body" weight="regular" />
        );
      },
    },
    {
      title: ProjectTableColumnLabelModifier.projectNumber,
      dataIndex: 'projectNumber',
      key: PROJECT_TABLE_COLUMN_KEYS.PROJECT_NUMBER,
      width: 200,
      render: (_, record) => {
        return (
          <Text text={record.projectNumber} variant="body" weight="regular" />
        );
      },
    },
    ...(requestParams.sheetType &&
    ['RS', 'SS'].includes(requestParams.sheetType)
      ? ([
          {
            title: ProjectTableColumnLabelModifier.budgetData,
            dataIndex: 'budgetData',
            key: PROJECT_TABLE_COLUMN_KEYS.BUDGET_DATA,
            width: 200,
            render: (_, record) => {
              return <BudgetAmountGraph budgetAmountData={record.budgetData} />;
            },
          },
          {
            title:
              ProjectTableColumnLabelModifier.finalizedAmountForInitialBudget,
            dataIndex: 'finalizedAmountForInitialBudget',
            key: PROJECT_TABLE_COLUMN_KEYS.FINALIZED_AMOUNT_FOR_INITIAL_BUDGET,
            defaultSortOrder: getSortOrder('finalizedAmountForInitialBudget'),
            sorter: true,
            width: 280,
            render: (_, record) => {
              const negativeFinalizedAmountForInitialBudgetCount =
                record.negativeFinalizedAmountForInitialBudgetCount;

              const { isAggregateValueZero, fallbackValue } =
                handleZeroValueWithNegativeCount(
                  record.finalizedAmountForInitialBudget,
                  {
                    negativeCount:
                      negativeFinalizedAmountForInitialBudgetCount ?? 0,
                    isDebugMode,
                  },
                );
              if (isAggregateValueZero) {
                return fallbackValue;
              }
              return renderAmountText(record.finalizedAmountForInitialBudget);
            },
          },
          {
            title: ProjectTableColumnLabelModifier.organizationLabel,
            dataIndex: 'organizationLabel',
            key: PROJECT_TABLE_COLUMN_KEYS.ORGANIZATION_LABEL,
            render: (_, record) => {
              return record.organization.flatten.names.join(
                NEST_ITEM_CONNECTOR,
              );
            },
            width: 500,
            defaultSortOrder: getSortOrder('organizationLabel'),
            sorter: true,
          },
          {
            title: ProjectTableColumnLabelModifier.purpose,
            dataIndex: 'purpose',
            key: PROJECT_TABLE_COLUMN_KEYS.PURPOSE,
            render: (_, record) => {
              return (
                <Text
                  maxRows={3}
                  weight="regular"
                  maxWidth={400}
                  text={record.purpose}
                />
              );
            },
          },
          {
            title: ProjectTableColumnLabelModifier.task,
            dataIndex: 'task',
            key: PROJECT_TABLE_COLUMN_KEYS.TASK,
            width: 250,
            render: (_, record) => {
              return (
                <Text
                  maxRows={3}
                  weight="regular"
                  maxWidth={250}
                  text={record.task}
                />
              );
            },
          },
          {
            title: ProjectTableColumnLabelModifier.previousYearExecutedAmounts,
            dataIndex: 'previousYearExecutedAmounts',
            key: PROJECT_TABLE_COLUMN_KEYS.PREVIOUS_YEAR_EXECUTED_AMOUNTS,
            defaultSortOrder: getSortOrder('previousYearExecutedAmounts'),
            sorter: true,
            width: 280,
            render: (_, record) => {
              const { isAggregateValueZero, fallbackValue } =
                handleZeroValueWithNegativeCount(
                  record.previousYearExecutionAmount,
                  {
                    negativeCount:
                      record.negativePreviousYearExecutionAmountCount ?? 0,
                    isDebugMode,
                  },
                );
              if (isAggregateValueZero) {
                return fallbackValue;
              }
              return renderAmountText(record.previousYearExecutionAmount);
            },
          },
          {
            title: ProjectTableColumnLabelModifier.previousYearExecutionRate,
            dataIndex: 'previousYearExecutionRate',
            key: PROJECT_TABLE_COLUMN_KEYS.PREVIOUS_YEAR_EXECUTION_RATE,
            defaultSortOrder: getSortOrder('previousYearExecutionRate'),
            sorter: true,
            width: 280,
            render: (_, record) =>
              formatFraction(record.previousYearExecutionRate, 100, {
                maximumFractionDigits: 1,
                useNegativeSign: true,
              }),
          },
          {
            title: ProjectTableColumnLabelModifier.nextYearRequestedAmount,
            dataIndex: 'nextYearRequestedAmount',
            key: PROJECT_TABLE_COLUMN_KEYS.NEXT_YEAR_REQUESTED_AMOUNT,
            defaultSortOrder: getSortOrder('nextYearRequestedAmount'),
            sorter: true,
            width: 280,
            render: (_, record) => {
              const negativeRequestedAmountCount =
                record.negativeRequestedAmountCount;

              const { isAggregateValueZero, fallbackValue } =
                handleZeroValueWithNegativeCount(record.requestedAmount, {
                  negativeCount: negativeRequestedAmountCount ?? 0,
                  isDebugMode,
                });
              if (isAggregateValueZero) {
                return fallbackValue;
              }
              return renderAmountText(record.requestedAmount);
            },
          },
          {
            title:
              ProjectTableColumnLabelModifier.finalizedAmountForSupplementaryBudget,
            dataIndex: 'finalizedAmountForSupplementaryBudget',
            key: PROJECT_TABLE_COLUMN_KEYS.FINALIZED_AMOUNT_FOR_SUPPLEMENTARY_BUDGET,
            defaultSortOrder: getSortOrder(
              'finalizedAmountForSupplementaryBudget',
            ),
            sorter: true,
            width: 280,
            render: (_, record) => {
              const negativeFinalizedAmountForSupplementaryBudgetCount =
                record.negativeFinalizedAmountForSupplementaryBudgetCount;

              const { isAggregateValueZero, fallbackValue } =
                handleZeroValueWithNegativeCount(
                  record.finalizedAmountForSupplementaryBudget,
                  {
                    negativeCount:
                      negativeFinalizedAmountForSupplementaryBudgetCount ?? 0,
                    isDebugMode,
                  },
                );
              if (isAggregateValueZero) {
                return fallbackValue;
              }
              return renderAmountText(
                record.finalizedAmountForSupplementaryBudget,
              );
            },
          },
          {
            title:
              ProjectTableColumnLabelModifier.finalizedAmountForCarriedForward,
            dataIndex: 'finalizedAmountForCarriedForward',
            key: PROJECT_TABLE_COLUMN_KEYS.FINALIZED_AMOUNT_FOR_CARRIED_FORWARD,
            defaultSortOrder: getSortOrder('finalizedAmountForCarriedForward'),
            sorter: true,
            width: 280,
            render: (_, record) => {
              const negativeFinalizedAmountForCarriedForwardCount =
                record.negativeFinalizedAmountForCarriedForwardCount;

              const { isAggregateValueZero, fallbackValue } =
                handleZeroValueWithNegativeCount(
                  record.finalizedAmountForCarriedForward,
                  {
                    negativeCount:
                      negativeFinalizedAmountForCarriedForwardCount ?? 0,
                    isDebugMode,
                  },
                );
              if (isAggregateValueZero) {
                return fallbackValue;
              }
              return renderAmountText(record.finalizedAmountForCarriedForward);
            },
          },
          {
            title: ProjectTableColumnLabelModifier.finalizedAmountForReserve,
            dataIndex: 'finalizedAmountForReserve',
            key: PROJECT_TABLE_COLUMN_KEYS.FINALIZED_AMOUNT_FOR_RESERVE,
            defaultSortOrder: getSortOrder('finalizedAmountForReserve'),
            sorter: true,
            width: 280,
            render: (_, record) => {
              const negativeFinalizedAmountForReserveCount =
                record.negativeFinalizedAmountForReserveCount;

              const { isAggregateValueZero, fallbackValue } =
                handleZeroValueWithNegativeCount(
                  record.finalizedAmountForReserve,
                  {
                    negativeCount: negativeFinalizedAmountForReserveCount ?? 0,
                    isDebugMode,
                  },
                );
              if (isAggregateValueZero) {
                return fallbackValue;
              }
              return renderAmountText(record.finalizedAmountForReserve);
            },
          },
          {
            title: ProjectTableColumnLabelModifier.finalizedAmount,
            dataIndex: 'finalizedAmount',
            key: PROJECT_TABLE_COLUMN_KEYS.FINALIZED_AMOUNT,
            defaultSortOrder: getSortOrder('finalizedAmount'),
            sorter: true,
            width: 280,
            render: (_, record) => {
              const negativeFinalizedAmountCount =
                record.negativeFinalizedAmountCount;

              const { isAggregateValueZero, fallbackValue } =
                handleZeroValueWithNegativeCount(record.finalizedAmount, {
                  negativeCount: negativeFinalizedAmountCount ?? 0,
                  isDebugMode,
                });
              if (isAggregateValueZero) {
                return fallbackValue;
              }
              return renderAmountText(record.finalizedAmount);
            },
          },
          {
            title: ProjectTableColumnLabelModifier.startYear,
            dataIndex: 'startYear',
            key: PROJECT_TABLE_COLUMN_KEYS.START_YEAR,
            width: 150,
            defaultSortOrder: getSortOrder('startYear'),
            sorter: true,
            render: (_, record) => {
              return (
                <Text text={record.startYear} variant="body" weight="regular" />
              );
            },
          },
          {
            title: ProjectTableColumnLabelModifier.endYear,
            dataIndex: 'endYear',
            key: PROJECT_TABLE_COLUMN_KEYS.END_YEAR,
            width: 150,
            defaultSortOrder: getSortOrder('endYear'),
            sorter: true,
            render: (_, record) => {
              return (
                <Text text={record.endYear} variant="body" weight="regular" />
              );
            },
          },
        ] satisfies ColumnsType<Project>)
      : []),
    ...(requestParams.sheetType === 'KS'
      ? ([
          {
            title: ProjectTableColumnLabelModifier.fundOwner,
            dataIndex: 'fundOwner',
            key: PROJECT_TABLE_COLUMN_KEYS.FUND_OWNER,
            width: 200,
            render: (_, record) => {
              return (
                <Text
                  maxRows={3}
                  weight="regular"
                  maxWidth={400}
                  text={record?.fundOwner}
                />
              );
            },
          },
          {
            title: ProjectTableColumnLabelModifier.fiscalYearOfCreation,
            dataIndex: 'fiscalYearOfCreation',
            key: PROJECT_TABLE_COLUMN_KEYS.FISCAL_YEAR_OF_CREATION,
            defaultSortOrder: getSortOrder('fiscalYearOfCreation'),
            sorter: true,
            width: 120,
            render: (_, record) => {
              return (
                <Text
                  weight="regular"
                  text={record?.compositionBackgroundsFiscalYearOfCreation}
                />
              );
            },
          },
          {
            title:
              ProjectTableColumnLabelModifier.endDateOfNewApplicationReceiving,
            dataIndex: 'endDateOfNewApplicationReceiving',
            key: PROJECT_TABLE_COLUMN_KEYS.END_DATE_OF_NEW_APPLICATION_RECEIVING,
            defaultSortOrder: getSortOrder('endDateOfNewApplicationReceiving'),
            sorter: true,
            width: 250,
            render: (_, record) => {
              return (
                <Text
                  maxRows={3}
                  weight="regular"
                  maxWidth={400}
                  text={record?.scheduledEndEndDateOfNewApplicationReceiving}
                />
              );
            },
          },
          {
            title: ProjectTableColumnLabelModifier.scheduledEndDate,
            dataIndex: 'scheduledEndDate',
            key: PROJECT_TABLE_COLUMN_KEYS.SCHEDULED_END_DATE,
            defaultSortOrder: getSortOrder('scheduledEndDate'),
            sorter: true,
            width: 200,
            render: (_, record) => {
              return (
                <Text
                  maxRows={3}
                  weight="regular"
                  maxWidth={400}
                  text={record?.scheduledEndEndDate}
                />
              );
            },
          },
          {
            title: ProjectTableColumnLabelModifier.administrativeExpenses,
            dataIndex: 'previousYearAdministrativeExpense',
            defaultSortOrder: getSortOrder('previousYearAdministrativeExpense'),
            sorter: true,
            key: PROJECT_TABLE_COLUMN_KEYS.ADMINISTRATIVE_EXPENSES,
            width: 200,
            render: (_, record) => {
              return renderAmountText(record.previousYearAdministrativeExpense);
            },
          },
          {
            title: ProjectTableColumnLabelModifier.previousYearTotalIncome,
            dataIndex: 'previousYearTotalIncome',
            key: PROJECT_TABLE_COLUMN_KEYS.PREVIOUS_YEAR_TOTAL_INCOME,
            defaultSortOrder: getSortOrder('previousYearTotalIncome'),
            sorter: true,
            width: 150,
            render: (_, record) => {
              const negativePreviousYearTotalIncomeCount =
                record.negativePreviousYearTotalIncomeCount;

              const { isAggregateValueZero, fallbackValue } =
                handleZeroValueWithNegativeCount(
                  record.previousYearTotalIncome,
                  {
                    negativeCount: negativePreviousYearTotalIncomeCount ?? 0,
                    isDebugMode,
                  },
                );
              if (isAggregateValueZero) {
                return fallbackValue;
              }
              return renderAmountText(record.previousYearTotalIncome);
            },
          },
          {
            title: ProjectTableColumnLabelModifier.previousYearTotalExpense,
            dataIndex: 'previousYearTotalExpense',
            key: PROJECT_TABLE_COLUMN_KEYS.PREVIOUS_YEAR_TOTAL_EXPENSE,
            defaultSortOrder: getSortOrder('previousYearTotalExpense'),
            sorter: true,
            width: 200,
            render: (_, record) => {
              const negativePreviousYearTotalExpenseCount =
                record.negativePreviousYearTotalExpenseCount;

              const { isAggregateValueZero, fallbackValue } =
                handleZeroValueWithNegativeCount(
                  record.previousYearTotalExpense,
                  {
                    negativeCount: negativePreviousYearTotalExpenseCount ?? 0,
                    isDebugMode,
                  },
                );
              if (isAggregateValueZero) {
                return fallbackValue;
              }
              return renderAmountText(record.previousYearTotalExpense);
            },
          },
          {
            title:
              ProjectTableColumnLabelModifier.amountReturnedToTheNationalTreasury,
            dataIndex: 'previousYearAmountReturnedToTheNationalTreasury',
            defaultSortOrder: getSortOrder(
              'previousYearAmountReturnedToTheNationalTreasury',
            ),
            sorter: true,
            key: PROJECT_TABLE_COLUMN_KEYS.AMOUNT_RETURNED_TO_THE_NATIONAL_TREASURY,
            width: 200,
            render: (_, record) => {
              return renderAmountText(
                record.previousYearAmountReturnedToTheNationalTreasury,
              );
            },
          },
          {
            title: ProjectTableColumnLabelModifier.fundSheetNumber,
            dataIndex: 'fundSheetNumber',
            key: PROJECT_TABLE_COLUMN_KEYS.FUND_SHEET_NUMBER,
            defaultSortOrder: getSortOrder('fundSheetNumber'),
            sorter: true,
            width: 120,
            render: (_, record) => {
              return (
                <Text
                  maxRows={3}
                  weight="regular"
                  maxWidth={400}
                  text={record?.fundSheetNumber}
                />
              );
            },
          },
          {
            title: ProjectTableColumnLabelModifier.fundSheetBranchNumber,
            dataIndex: 'fundSheetBranchNumber',
            key: PROJECT_TABLE_COLUMN_KEYS.FUND_SHEET_BRANCH_NUMBER,
            width: 100,
            render: (_, record) => {
              return (
                <Text
                  maxRows={3}
                  weight="regular"
                  maxWidth={400}
                  text={record?.fundSheetBranchNumber}
                />
              );
            },
          },
          {
            title: ProjectTableColumnLabelModifier.operationForms,
            dataIndex: 'operationForms',
            key: PROJECT_TABLE_COLUMN_KEYS.OPERATION_FORMS,
            width: 150,
            render: (_, { operationForms }) => {
              const operationFormsText = operationForms?.length
                ? operationForms
                    .map((form) => OperationFormLabelModifier[form])
                    .join(', ')
                : null;

              return (
                <Text
                  maxRows={3}
                  weight="regular"
                  maxWidth={400}
                  text={operationFormsText}
                />
              );
            },
          },
          {
            title: ProjectTableColumnLabelModifier.businessForms,
            dataIndex: 'businessForms',
            key: PROJECT_TABLE_COLUMN_KEYS.BUSINESS_FORMS,
            width: 150,
            render: (_, { businessForms }) => {
              const businessFormsText = businessForms?.length
                ? businessForms
                    .map((form) => BusinessFormLabelModifier[form])
                    .join(', ')
                : null;

              return (
                <Text
                  maxRows={3}
                  weight="regular"
                  maxWidth={400}
                  text={businessFormsText}
                />
              );
            },
          },
          {
            title: ProjectTableColumnLabelModifier.beginningOfTheFundBalance,
            dataIndex: 'beginningOfTheFundBalance',
            key: PROJECT_TABLE_COLUMN_KEYS.BEGINNING_OF_THE_FUND_BALANCE,
            defaultSortOrder: getSortOrder('beginningOfTheFundBalance'),
            sorter: true,
            width: 150,
            render: (_, record) => {
              return renderAmountText(record.beginningOfTheFundBalance);
            },
          },
          {
            title: ProjectTableColumnLabelModifier.ownershipRatio,
            dataIndex: 'ownershipRatio',
            key: PROJECT_TABLE_COLUMN_KEYS.OWNERSHIP_RATIO,
            defaultSortOrder: getSortOrder('ownershipRatio'),
            sorter: true,
            render: (_, record) =>
              formatOwnershipRatioPercentage(record.ownershipRatio),
            width: 100,
          },
          {
            title:
              ProjectTableColumnLabelModifier.previousYearAdministrativeExpenseRate,
            dataIndex: 'previousYearAdministrativeExpenseRate',
            key: PROJECT_TABLE_COLUMN_KEYS.PREVIOUS_YEAR_ADMINISTRATIVE_EXPENSE_RATE,
            defaultSortOrder: getSortOrder(
              'previousYearAdministrativeExpenseRate',
            ),
            sorter: true,
            width: 200,
            render: (_, record) =>
              formatFraction(
                record.previousYearAdministrativeExpenseRate,
                100,
                {
                  maximumFractionDigits: 1,
                  useNegativeSign: true,
                },
              ),
          },
          {
            title: ProjectTableColumnLabelModifier.previousYearDivergenceRate,
            dataIndex: 'previousYearDivergenceRate',
            key: PROJECT_TABLE_COLUMN_KEYS.PREVIOUS_YEAR_DIVERGENCE_RATE,
            defaultSortOrder: getSortOrder('previousYearDivergenceRate'),
            sorter: true,
            width: 200,
            render: (_, record) =>
              formatFraction(record.previousYearDivergenceRate, 100, {
                maximumFractionDigits: 1,
                useNegativeSign: true,
              }),
          },
        ] satisfies ColumnsType<Project>)
      : []),
    {
      title: ProjectTableColumnLabelModifier.mainPayment,
      dataIndex: 'mainPayment',
      key: PROJECT_TABLE_COLUMN_KEYS.MAIN_PAYMENT,
      width: 380,
      render: (_, record) => {
        return (
          <Space direction="vertical" size="small">
            <Space>
              <Text weight="medium" text={record.paymentData.name} />
            </Space>
            <Text
              variant="footnote"
              color="secondary"
              text={record.paymentData.corporateNumber}
            />
          </Space>
        );
      },
    },
    {
      title: ProjectTableColumnLabelModifier.paymentAmount,
      dataIndex: 'paymentAmount',
      key: PROJECT_TABLE_COLUMN_KEYS.PAYMENT_AMOUNT,
      defaultSortOrder: getSortOrder('paymentAmount'),
      sorter: true,
      width: 200,
      render: (_, record) => {
        const negativeAmountCount = record.paymentData.negativeAmountCount;

        const { isAggregateValueZero, fallbackValue } =
          handleZeroValueWithNegativeCount(record.paymentData.amount, {
            negativeCount: negativeAmountCount ?? 0,
            isDebugMode,
          });
        if (isAggregateValueZero) {
          return fallbackValue;
        }
        return renderAmountText(record.paymentData.amount);
      },
    },
  ];
  const columns = columnsSettings.flatMap(
    (columnsSetting) =>
      allColumns.find((column) => column.key === columnsSetting) ?? [],
  );

  const handleSingleSorter = useCallback(
    (sorter: SorterResult<Project>) => {
      if (sorter.order && sorter.columnKey) {
        const prefix = sorter.order === 'descend' ? '-' : '';
        const ordering = `${prefix}${getApiOrderingKey(sorter.columnKey.toString())}`;
        if (policyId) {
          setSorting(ordering);
        } else {
          setSearchQueryStates((prev) => ({
            ...prev,
            page: 1,
            ordering,
          }));
        }
      } else {
        if (policyId) {
          setSorting('');
        } else {
          setSearchQueryStates((prev) => ({
            ...prev,
            page: 1,
            ordering: null,
          }));
        }
      }
    },
    [policyId, setSorting, setSearchQueryStates],
  );

  useHandle403ErrorWhenSearchingForPS(projectsData);

  const tableProps = useMemo<TableProps<Project>>(
    () => ({
      columns: [...columns, tableActionsColumn],
      dataSource: projectsData.data?.results,
      rowKey: 'id',
      pagination: {
        current: policyId
          ? Number(childTablePageNumber)
          : Number(requestParams.page),
        total: projectsData.data?.count,
        showSizeChanger: false,
        pageSize: Number(pageSize),
      },
      scroll: { x: 'max-content' },
      style: { width: '100%' },
      onChange: (pagination, _, sorter, extra) => {
        switch (extra.action) {
          case 'paginate':
            if (pagination.current) {
              if (policyId) {
                setChildTablePageNumber(pagination.current.toString());
                scrollToTop?.();
              } else {
                setSearchQueryStates((prev) => ({
                  ...prev,
                  page: pagination.current,
                }));
              }
            }
            break;
          case 'sort':
            invariant(!Array.isArray(sorter), t('unreachable-code'));
            handleSingleSorter(sorter);
            break;
          default:
        }
      },
    }),
    [
      columns,
      tableActionsColumn,
      projectsData.data?.results,
      projectsData.data?.count,
      pageSize,
      setSearchQueryStates,
      handleSingleSorter,
      childTablePageNumber,
      policyId,
      scrollToTop,
      requestParams,
      t,
    ],
  );

  if (projectsData.isPending || !projectsData.data) {
    return <ProjectsTableSkeleton />;
  }

  return (
    <Table
      aria-label="projects-table"
      key={JSON.stringify(columns)}
      {...tableProps}
    />
  );
};
